
import * as React from 'react';
import { IEventModifier, IEventType } from '@ms/telemetry-manager/lib/EventBase';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';
import { telemetryContextControl } from '../../controls/telemetryContextControl';
import { ControlHandlerProvider, IControlFaceted } from '../decorators/withControlHandler';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { getTelemetryManager } from '../../operations/telemetry/getTelemetryManager';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IComponentDecorator, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { readItems } from '../../operations/readItems';

export interface ITelemetryProviderProps {
  withContext: IEventModifier<IEventContext>;
}

const DEFAULT_CONTEXT: IEventContext = {};

/**
 * Component which provides telemetry context to all descendant components.
 * Specify either an object or a callback to the `withContext` prop.
 * If passed an object, `withContext` can supply default values for downstream scenarios.
 * If passed a callback `withContext` may be used to override values for downstream scenarios.
 */
export class TelemetryProvider extends React.PureComponent<ITelemetryProviderProps> {
  private _controlHandlers: IControlFaceted;

  constructor(props: ITelemetryProviderProps) {
    super(props);

    this._controlHandlers = telemetryContextControl.pack(this._telemetryContextControlHandler);
  }

  public render(): JSX.Element {
    return (
      <ControlHandlerProvider
        controlHandlers={ this._controlHandlers }
      >
        { this.props.children }
      </ControlHandlerProvider>
    );
  }

  private _telemetryContextControlHandler = (
    itemCache: IItemCache,
    props: typeof telemetryContextControl.inputType
  ): typeof telemetryContextControl.returnType => {
    return {
      withContext: this.props.withContext
    };
  }
}

export interface ITelemetryConsumerStateProps {
  telemetry: <T extends IEventType<any>>(eventType: T) => T; // tslint:disable-line:no-any
}

export function withTelemetry<TInjectedProps = ITelemetryConsumerStateProps, TOwnProps = {}>(
  mapTelemetryToProps?: (telemetryProps: ITelemetryConsumerStateProps, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    const EvaluatedControlHandlers = connectToItemCache((itemCache: IItemCache, props: TOwnProps): TInjectedProps => {
      const { dispatch } = itemCache;

      const telemetryControlHandler = itemCache.demandItemFacet(telemetryContextControl, undefined);

      const telemetryStateProps: ITelemetryConsumerStateProps = {
        telemetry: <T extends IEventType<any>>(eventType: T): T => { // tslint:disable-line:no-any
          const manager = dispatch(getTelemetryManager);

          const {
            withContext
          } = dispatch(readItems((itemCache2: IItemCache) => {
            return telemetryContextControl.evaluate(telemetryControlHandler)(itemCache, {
              itemKey: undefined,
              withContext: DEFAULT_CONTEXT
            });
          }));

          return eventType.withManager(manager).withContext(withContext);
        }
      };

      return mapTelemetryToProps ?
        mapTelemetryToProps(telemetryStateProps, props as {} as TOwnProps) :
        telemetryStateProps as {} as TInjectedProps;
    })(component);

    return EvaluatedControlHandlers as {} as IDecoratedComponentType<P, TInjectedProps, TOwnProps>;
  };
}

export interface ITelemetryConsumerProps extends IRenderProps<ITelemetryConsumerStateProps> { }

export const TelemetryConsumer: React.ComponentType<ITelemetryConsumerProps> =
  withTelemetry()((props: ITelemetryConsumerProps & ITelemetryConsumerStateProps): JSX.Element | null => {
    return renderChildrenWithProps(props.children, {
      telemetry: props.telemetry
    });
  });
